import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxPaginationModule } from 'ngx-pagination';
import { Subject, takeUntil } from 'rxjs';
import { ApiServiceService } from '../api-service.service';
import { CommonDropdownSearchComponent } from '../common-component-dropdown-search/common-component-dropdown-search.component';
import { CommonPaginationComponent } from '../common-component-pagination/common-component-pagination.component';
import { CommonSearchComponent } from '../common-component-search/common-component-search.component';
import { StateWiseUserSelectComponentComponent } from '../state-wise-user-select-component/state-wise-user-select-component.component';
import { UserAssignmentSelectorComponent } from '../user-assignment-selector/user-assignment-selector.component';

@Component({
  selector: 'app-ticket-assignment-master',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    NgxPaginationModule,
    MatTooltip,
    MatFormFieldModule,
    MatInputModule,
    StateWiseUserSelectComponentComponent,
    UserAssignmentSelectorComponent,
    CommonDropdownSearchComponent,
    CommonPaginationComponent,
    CommonSearchComponent,
    MatAutocompleteModule,
    NgxMatSelectSearchModule,
    MatError,
    MatSelectModule,
  ],
  templateUrl: './ticket-assignment-master.component.html',
  styleUrls: ['./ticket-assignment-master.component.scss'],
})
export class TicketAssignmentMasterComponent implements OnInit, OnDestroy {
  private _onDestroy = new Subject<void>();

  listOfTicketAssignmentMaster: {
    _id: string;
    name: string;
    displayName: string;
    description: string;
    states: string[];
    assignments: any[];
    stateNames?: string[];
  }[] = [];

  allUsers: {
    _id: string;
    fullName: string;
    customerEmail: string;
    assignments: { state: string; dealers: string }[];
    parsedAssignments?: { stateName: string; dealers: string }[];
    totalIPTickets: number;
    totalOHTickets: number;
  }[] = [];

  filteredUsers: {
    _id: string;
    fullName: string;
    customerEmail: string;
    assignments: { state: string; dealers: string }[];
  }[] = [];

  stateList = []; // From API
  dealerMap: Record<string, any[]> = {};

  updateTicketAssignmentForm: FormGroup = this.formBuilder.group({});
  updateUserStateAssignmentForm: FormGroup = this.formBuilder.group({});
  defaultTicketAssignToUserSelectFilterCtrl = new FormControl('');
  searchStateWiseUser = '';
  searchStateWiseUserState = '';
  sizeForStateWiseUserPopup = 10;
  pageForStateWiseUserPopup = 1;
  totalItemsForStateWiseUserPopup = 0;

  updateDefaultTicketAssignToUserForm: FormGroup = this.formBuilder.group({});
  updateFotaNotRequiredStateListForm: FormGroup = this.formBuilder.group({});

  userPermission = [];
  MyActivationsUpdateMasterAccess = false;

  constructor(
    public apiMicroservice: ApiServiceService,
    private formBuilder: FormBuilder,
    public router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.checkPermission();
    if (this.MyActivationsUpdateMasterAccess) {
      this.setTicketAssignmentMasterForm();
      this.setUserStateAssignmentForm();
      this.setDefaultTicketAssignedToUserForm();
      this.setFotaSkipStatesForm();
      this.getStates();
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (
          element.pageName === 'My AIS 140 Tickets Master Access' &&
          element.update === true
        ) {
          this.MyActivationsUpdateMasterAccess = true;
        }
        if (
          element.pageName === 'My AIS 140 Tickets Master Access' &&
          element.update === false
        ) {
          this.navigateBack();
        }
      }
    );
  }

  async getStates() {
    this.apiMicroservice.getAllGovtServerList().subscribe((res: any) => {
      if (res.status) {
        this.stateList = res.data.map((element) => ({
          key: element.state,
          name: element.state,
          value: element.stateAbbreviation,
        }));
      }
      this.getTicketAssignmentMasterList();
      this.getUserAssignmentList();
      this.getFotaNotRequiredStateList();
      this.defaultTicketAssignToUserSelectFilterCtrl.valueChanges
        .pipe(takeUntil(this._onDestroy))
        .subscribe((value) => this.filterUsers(value));
    });
  }

  compareFn = (a: any, b: any) => a === b;

  loadDealers(state: string) {
    if (this.dealerMap[state]) {
      return;
    }

    this.apiMicroservice.getDealerByState(state).subscribe((res: any) => {
      if (res.status) {
        this.dealerMap[state] = res.data.map(String);
      }
    });
  }

  getTicketAssignmentMasterList() {
    this.apiMicroservice.getTicketAssignmentMasterList().subscribe((res) => {
      if (res.status) {
        this.listOfTicketAssignmentMaster = res.data;
        this.listOfTicketAssignmentMaster.forEach((assignmentMaster) => {
          if (assignmentMaster.states && assignmentMaster.states.length > 0) {
            assignmentMaster.stateNames = assignmentMaster.states.map(
              (state) => {
                const stateObj = this.stateList.find((s) => s.value === state);
                return stateObj ? stateObj.name : state;
              }
            );
          }
        });
      }
    });
  }

  setTicketAssignmentMasterForm() {
    this.updateTicketAssignmentForm = this.formBuilder.group({
      _id: ['', Validators.required],
      name: ['', Validators.required],
      displayName: ['', Validators.required],
      states: [''],
    });
  }

  openTicketAssignmentPopup(user: any) {
    this.updateTicketAssignmentForm.patchValue({
      _id: user._id,
      name: user.name,
      displayName: user.displayName,
      states: [...user.states],
    });
    document
      ?.getElementById('updateTicketAssignmentPopup')
      ?.parentElement?.classList.add('active');
  }

  closeTicketAssignmentPopup() {
    this.setTicketAssignmentMasterForm();

    document
      ?.getElementById('updateTicketAssignmentPopup')
      ?.parentElement?.classList.remove('active');
  }

  saveAssignment() {
    this.apiMicroservice
      .updateTicketAssignmentMasterList(
        this.updateTicketAssignmentForm.value._id,
        this.updateTicketAssignmentForm.value.states
      )
      .subscribe((res) => {
        if (res.status) {
          this.closeTicketAssignmentPopup();
          this.ngOnInit();
        }
      });
  }

  getUserAssignmentList() {
    this.apiMicroservice
      .userAssignmentList(
        this.searchStateWiseUser,
        this.searchStateWiseUserState
      )
      .subscribe((res) => {
        if (res.status) {
          this.allUsers = res.data;
          this.totalItemsForStateWiseUserPopup = res.data.length;
          this.allUsers = this.allUsers.map((user) => {
            const parsedAssignments = user.assignments.map((assignment) => {
              const stateObj = this.stateList.find(
                (s) => s.value === assignment.state
              );
              const stateName = stateObj ? stateObj.name : assignment.state;
              return {
                stateName: stateName,
                dealers: assignment.dealers,
              };
            });
            return {
              ...user,
              parsedAssignments: parsedAssignments,
            };
          });

          if (this.filteredUsers.length === 0) {
            this.filteredUsers = [...this.allUsers];
          }

          this.cdr.detectChanges();
          this.getDefaultTicketAssignedToUser();
        }
      });
  }

  filterUsers(search: string) {
    if (!search) {
      this.filteredUsers = [...this.allUsers];
      return;
    }

    search = search.toLowerCase();

    this.filteredUsers = this.allUsers.filter((user) =>
      user.fullName.toLowerCase().includes(search)
    );
  }

  onStateWiseSelectionChange(event: string) {
    this.searchStateWiseUserState = event;
    this.pageForStateWiseUserPopup = 1;
    this.getUserAssignmentList();
  }

  stateWiseUserListOnSearch(event: string) {
    this.searchStateWiseUser = event;
    this.pageForStateWiseUserPopup = 1;
    this.getUserAssignmentList();
  }

  onPageChangeForStateWiseUserPopup(event) {
    this.pageForStateWiseUserPopup = event;
  }

  onSizeChangeForStateWiseUserPopup(event) {
    this.pageForStateWiseUserPopup = 1;
    this.sizeForStateWiseUserPopup = event;
  }

  setUserStateAssignmentForm() {
    this.updateUserStateAssignmentForm = this.formBuilder.group({
      _id: ['', Validators.required],
      name: ['', Validators.required],
      displayName: ['', Validators.required],
      assignments: [[]],
    });
  }

  setDefaultTicketAssignedToUserForm() {
    this.updateDefaultTicketAssignToUserForm = this.formBuilder.group({
      _id: ['', Validators.required],
    });
  }

  setFotaSkipStatesForm() {
    this.updateFotaNotRequiredStateListForm = this.formBuilder.group({
      states: [''],
    });
  }

  getDefaultTicketAssignedToUser() {
    this.apiMicroservice.getDefaultTicketAssignedToUser().subscribe((res) => {
      if (res.status) {
        setTimeout(() => {
          this.updateDefaultTicketAssignToUserForm.patchValue({
            _id: res.data,
          });
        });
        this.cdr.detectChanges();
        this.updateDefaultTicketAssignToUserForm.updateValueAndValidity();
        this.cdr.detectChanges();
      }
    });
  }

  updateDefaultTicketAssignedToUser() {
    if (this.updateDefaultTicketAssignToUserForm.invalid) {
      this.updateDefaultTicketAssignToUserForm.markAllAsTouched();

      return;
    }
    const { _id } = this.updateDefaultTicketAssignToUserForm.value;
    this.apiMicroservice
      .updateDefaultTicketAssignedToUser(_id)
      .subscribe((res) => {
        if (res.status) {
          this.apiMicroservice.openSnackbar(
            'Default ticket assigned to user updated successfully'
          );
        } else {
          this.apiMicroservice.openSnackbar(
            'Failed to update default ticket assigned to user'
          );
        }
        this.getDefaultTicketAssignedToUser();
      });
  }

  getFotaNotRequiredStateList() {
    this.apiMicroservice.getFotaNotRequiredStateList().subscribe((res) => {
      if (res.status) {
        setTimeout(() => {
          this.updateFotaNotRequiredStateListForm.patchValue({
            states: res.data,
          });
        });
        this.cdr.detectChanges();
        this.updateFotaNotRequiredStateListForm.updateValueAndValidity();
        this.cdr.detectChanges();
      }
    });
  }

  updateFotaNotRequiredStateList() {
    if (this.updateFotaNotRequiredStateListForm.invalid) {
      this.updateFotaNotRequiredStateListForm.markAllAsTouched();

      return;
    }
    const { states } = this.updateFotaNotRequiredStateListForm.value;
    this.apiMicroservice
      .updateFotaNotRequiredStateList(states)
      .subscribe((res) => {
        if (res.status) {
          this.apiMicroservice.openSnackbar(
            'FOTA skip states updated successfully'
          );
        } else {
          this.apiMicroservice.openSnackbar(
            'Failed to update FOTA skip states'
          );
        }
        this.getFotaNotRequiredStateList();
      });
  }

  openUserStateAssignmentPopup(user: any) {
    const assignments = user.assignments.map((assignment) => ({
      state: assignment.state,
      dealers: assignment.dealers.map(String),
    }));
    this.updateUserStateAssignmentForm.patchValue({
      _id: user._id,
      name: user.fullName,
      displayName: user.displayName,
      assignments: assignments,
    });

    // Preload dealers for existing states
    user.assignments.forEach((assignment: any) => {
      this.loadDealers(assignment.state);
    });

    document
      ?.getElementById('updateUserStateAssignmentPopup')
      ?.parentElement?.classList.add('active');
  }

  onAssignmentsChange(assignments: any[]) {
    this.updateUserStateAssignmentForm.patchValue({
      assignments,
    });
  }

  closeUserStateAssignmentPopup() {
    this.setUserStateAssignmentForm();

    document
      ?.getElementById('updateUserStateAssignmentPopup')
      ?.parentElement?.classList.remove('active');
  }

  saveUserStateAssignment() {
    const { _id, assignments } = this.updateUserStateAssignmentForm.value;
    assignments.forEach((assignment) => {
      if (!assignment.state) {
        this.apiMicroservice.openSnackbar(
          'State is required for all assignments'
        );
        return;
      }
    });
    this.apiMicroservice
      .saveAssignmentById(_id, assignments)
      .subscribe((res) => {
        if (res.status) {
          this.closeUserStateAssignmentPopup();
          this.ngOnInit();
        }
      });
  }

  remarkChange(event) {
    this.updateTicketAssignmentForm.patchValue({
      states: [...event.val],
    });
  }

  remarkChangeFotaSkippedStates(event) {
    this.updateFotaNotRequiredStateListForm.patchValue({
      states: [...event.val],
    });
  }

  redirect(to: string): void {
    this.router.navigate([to]);
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
